import React, { useState } from 'react';
import axios from 'axios';
import { GithubEntwurf } from '../types';
import { naechsterZustand, issueNummerAusUrl } from '../lib/githubEntwurf';
import './GithubTicketKarte.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface Props {
  entwurf: GithubEntwurf;
  /** Meldet den neuen Zustand (angelegt/verworfen) an die Nachrichtenliste */
  onChange?: (e: GithubEntwurf) => void;
}

/** Bestätigungskarte für einen vom Agenten vorbereiteten GitHub-Ticket-Entwurf. */
const GithubTicketKarte: React.FC<Props> = ({ entwurf, onChange }) => {
  const [titel, setTitel] = useState(entwurf.titel);
  const [beschreibung, setBeschreibung] = useState(entwurf.beschreibung);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const offen = entwurf.status === 'OFFEN';
  const nummer = issueNummerAusUrl(entwurf.issue_url);

  const ausfuehren = async (aktion: 'anlegen' | 'verwerfen') => {
    if (busy) return;
    setBusy(true);
    setFehler(null);
    try {
      const url = `${API_URL}/api/github-tickets/${entwurf.entwurf_id}/${aktion}`;
      const body = aktion === 'anlegen' ? { titel: titel.trim(), beschreibung } : undefined;
      const res = await axios.post(url, body);
      onChange?.(naechsterZustand(
        { ...entwurf, titel: titel.trim() || entwurf.titel, beschreibung },
        { issue_url: res.data?.issue_url },
        aktion,
      ));
    } catch (err) {
      const detail = axios.isAxiosError(err) ? err.response?.data?.detail : undefined;
      setFehler(typeof detail === 'string' && detail
        ? detail
        : 'Die Aktion ist fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="k-ghentwurf">
      <div className="k-ghentwurf__head">
        <span>GitHub-Ticket-Entwurf</span>
        {entwurf.typ && <span className="k-ghentwurf__typ">{entwurf.typ}</span>}
      </div>

      {offen ? (
        <>
          <label className="k-ghentwurf__label">
            Titel
            <input
              className="k-ghentwurf__feld"
              type="text"
              maxLength={300}
              value={titel}
              onChange={e => setTitel(e.target.value)}
              disabled={busy}
            />
          </label>
          <label className="k-ghentwurf__label">
            Beschreibung
            <textarea
              className="k-ghentwurf__feld k-ghentwurf__text"
              value={beschreibung}
              onChange={e => setBeschreibung(e.target.value)}
              disabled={busy}
            />
          </label>
          {fehler && <p className="k-ghentwurf__fehler" role="alert">{fehler}</p>}
          <div className="k-ghentwurf__aktionen">
            <button
              type="button"
              className="k-btn k-btn--primary"
              onClick={() => ausfuehren('anlegen')}
              disabled={busy || !titel.trim()}
            >
              Ticket anlegen
            </button>
            <button
              type="button"
              className="k-btn k-btn--ghost"
              onClick={() => ausfuehren('verwerfen')}
              disabled={busy}
            >
              Verwerfen
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="k-ghentwurf__label">
            Titel
            <div className="k-ghentwurf__ergebnis">{entwurf.titel}</div>
          </div>
          {entwurf.status === 'ANGELEGT' ? (
            <p className="k-ghentwurf__ergebnis">
              {nummer !== null ? `Ticket #${nummer} angelegt` : 'Ticket angelegt'}
              {entwurf.issue_url && (
                <>
                  {' · '}
                  <a href={entwurf.issue_url} target="_blank" rel="noopener noreferrer">
                    Auf GitHub öffnen
                  </a>
                </>
              )}
            </p>
          ) : (
            <p className="k-ghentwurf__ergebnis">Entwurf verworfen</p>
          )}
        </>
      )}
    </div>
  );
};

export default GithubTicketKarte;
